import axios from 'axios';
import axiosInstance from '../../../utils/axiosConfig';

// Get the API URL from environment variables or use a default
const getApiUrl = () => {
  const baseUrl = import.meta.env.VITE_API_URL || '';
  // Handle production case where VITE_API_URL might be '/api'
  if (baseUrl === '/api' || baseUrl === '') {
    return '/api/wallet';
  }
  return `${baseUrl}/api/wallet`;
};

const API_URL = getApiUrl();

console.log('🔧 API Configuration:', {
  envUrl: import.meta.env.VITE_API_URL,
  finalUrl: API_URL
});

/**
 * Wallet Service
 * 🏦 Handles all wallet-related API calls
 */
const walletService = {
  // Get all accounts
  getAccounts: async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(API_URL, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Handle different response formats
      let accountsData = [];
      let partnerAccountsData = [];
      
      if (Array.isArray(response.data)) {
        accountsData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        accountsData = response.data.data;
        // Check for partner accounts in the response
        if (response.data?.partnerAccounts && Array.isArray(response.data.partnerAccounts)) {
          partnerAccountsData = response.data.partnerAccounts;
        }
      } else if (response.data?.accounts && Array.isArray(response.data.accounts)) {
        accountsData = response.data.accounts;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        accountsData = response.data.data;
        // Check for partner accounts in the response
        if (response.data?.partnerAccounts && Array.isArray(response.data.partnerAccounts)) {
          partnerAccountsData = response.data.partnerAccounts;
        }
      }
      
      return {
        success: true,
        data: accountsData,
        partnerAccounts: partnerAccountsData
      };
    } catch (error) {
      console.error('❌ Error fetching accounts:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Create new account (updated to handle asset-liability linking)
  createAccount: async (accountData) => {
    try {
      console.log('➕ Creating account:', accountData);
      const token = localStorage.getItem('token');
      const response = await axios.post(API_URL, accountData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('✅ Account created:', response.data);
      
      // Handle the new response format with potential linked accounts
      const result = {
        success: true,
        data: response.data
      };
      
      // If a linked account was created, add it to the result
      if (response.data.data?.linkedAccount) {
        result.linkedAccount = response.data.data.linkedAccount;
        result.message = response.data.data.message;
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error creating account:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Update account
  updateAccount: async (id, accountData) => {
    try {
      console.log('🔄 Updating account:', { id, data: accountData });
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/${id}`, accountData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('✅ Account updated:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error updating account:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Delete account
  deleteAccount: async (id) => {
    try {
      console.log('🗑️ Deleting account:', id);
      const token = localStorage.getItem('token');
      
      // Ensure we're using the correct API URL
      const endpoint = `${API_URL}/${id}`;
      console.log('🔗 Delete endpoint:', endpoint);
      
      const response = await axios.delete(endpoint, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('✅ Account deleted:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error deleting account:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Upload and process statement
  uploadStatement: async (formData) => {
    try {
      console.log('📄 Processing statement upload:', {
        isFormData: formData instanceof FormData,
        formDataEntries: Array.from(formData.entries()).map(([key, value]) => ({
          key,
          value: value instanceof File ? `File: ${value.name}` : value
        }))
      });

      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/process-statement-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('✅ Upload response:', response.data);

      // Handle duplicates case
      if (response.data?.duplicates?.length > 0) {
        return {
          success: true,
          data: {
            ...response.data,
            message: `Processed with ${response.data.duplicates.length} duplicate transactions`
          }
        };
      }

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Statement upload error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        formData: formData ? 'Present' : 'Missing'
      });
      
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Get account details with transactions
  getAccountDetails: async (accountId, page = 1, limit = 50) => {
    try {
      console.log('🔍 Service: Fetching account details:', { accountId, page, limit });

      if (!accountId) {
        throw new Error('Account ID is required');
      }

      const token = localStorage.getItem('token');
      console.log('🔑 Token:', token ? 'Present' : 'Missing');

      const url = `${API_URL}/${accountId}`;
      console.log(' Request URL:', url);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit }
      });

      console.log('📦 Service: Response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Service: Error getting account details:', {
        accountId,
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Get all transactions
  getAllTransactions: async (page = 1, limit = 50) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting all transactions:', error);
      throw error;
    }
  },

  // First process the statement
  processStatement: async (formData) => {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/process-statement`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        },
      }
    );
    return response.data;
  },

  // Then create account with transactions
  createAccountWithTransactions: async (accountData) => {
    const token = localStorage.getItem('token');
    console.log('Creating account with transactions:', accountData);
    const response = await axios.post(
      `${API_URL}/create-with-transactions`,
      accountData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    console.log('Account creation response:', response.data);
    return response.data;
  },

  deleteAccountWithTransactions: async (id) => {
    try {
      console.log('🗑️ Deleting account and transactions:', id);
      const token = localStorage.getItem('token');
      
      // Ensure we're using the correct API URL
      const endpoint = `${API_URL}/${id}/with-transactions`;
      console.log('🔗 Delete with transactions endpoint:', endpoint);
      
      const response = await axios.delete(endpoint, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Account and transactions deleted:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error deleting account with transactions:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Disconnect a Plaid account
  disconnectPlaidAccount: async (account, includeTransactions = false) => {
    try {
      console.log('🔌 Disconnecting Plaid account:', account);
      
      // Check if this is actually a Plaid account
      if (!account || !account.isPlaidAccount) {
        console.error('Not a Plaid account or missing account data:', account);
        throw new Error('Not a Plaid account');
      }

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        throw new Error('Authentication token is missing');
      }
      
      // Get base API URL from environment or use default (without /api)
      // VITE_API_URL should be the base URL without /api
      const baseApiUrl = import.meta.env.VITE_API_URL 
        ? import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '') // Remove trailing /api if present
        : 'http://localhost:3001';
      
      console.log('Using base API URL:', baseApiUrl);
      
      // First, delete the wallet if it exists
      if (account._id) {
        try {
          console.log('🗑️ Deleting wallet:', account._id);
          // Ensure we're using the correct API endpoint
          const walletEndpoint = `${baseApiUrl}/api/wallet/${account._id}/with-transactions`;
          console.log('Wallet deletion endpoint:', walletEndpoint);
          
          const walletResponse = await axios.delete(walletEndpoint, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('✅ Successfully deleted wallet:', walletResponse.data);
        } catch (walletError) {
          // Log but continue if wallet deletion fails
          console.warn('⚠️ Wallet deletion failed:', walletError.message);
          
          // Try alternative endpoint if first one fails
          try {
            console.log('🔄 Trying alternative wallet deletion endpoint');
            const altEndpoint = `${baseApiUrl}/api/wallet/${account._id}`;
            console.log('Alternative wallet deletion endpoint:', altEndpoint);
            
            const altResponse = await axios.delete(altEndpoint, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            console.log('✅ Successfully deleted wallet (alternative endpoint):', altResponse.data);
          } catch (altError) {
            console.warn('⚠️ Alternative wallet deletion failed:', altError.message);
          }
        }
      }
      
      // Check if we have necessary identifiers
      const hasPlaidItemId = account.plaidItemId && account.plaidItemId.length > 0;
      const hasPlaidAccountId = account.plaidAccountId && account.plaidAccountId.length > 0;
      
      // If we don't have Plaid identifiers but includeTransactions is true, we can consider this a success
      // since we already deleted the wallet and transactions
      if (!hasPlaidItemId && !hasPlaidAccountId) {
        if (includeTransactions) {
          console.log('No Plaid identifiers found, but wallet and transactions were deleted successfully');
          return {
            success: true,
            data: { message: 'Account deleted successfully' }
          };
        } else {
          console.error('Missing Plaid identifiers:', account);
          throw new Error('No Plaid identifiers found for disconnection');
        }
      }
      
      // If includeTransactions is true, delete all transactions
      if (includeTransactions && hasPlaidAccountId) {
        try {
          console.log('🗑️ Deleting Plaid transactions for account:', account.plaidAccountId);
          // Ensure there's only one /api/ in the URL
          const txEndpoint = `${baseApiUrl}/api/plaid/transactions/${account.plaidAccountId}`;
          console.log('Transaction deletion endpoint:', txEndpoint);
          
          const deleteResponse = await axios.delete(txEndpoint, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log('✅ Successfully deleted Plaid transactions:', deleteResponse.data);
          
          // Check if standard transactions were deleted as well and log for debugging
          if (deleteResponse.data.details?.standardTransactionsDeleted) {
            console.log(`✅ Also deleted ${deleteResponse.data.details.standardTransactionsDeleted} standard transactions`);
          }
          
          // Small delay to ensure database operations are complete before proceeding
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (txError) {
          // If we get a 404, the account may have already been deleted - consider this a success
          if (txError.response && txError.response.status === 404) {
            console.log('⚠️ Transactions not found, account may have already been deleted');
          } else {
            console.error('Error deleting Plaid transactions:', {
              error: txError.message,
              response: txError.response?.data,
              status: txError.response?.status
            });
            // Continue with the rest of the disconnection even if transaction deletion fails
            console.warn('Continuing with Plaid disconnection despite transaction deletion failure');
          }
        }
      }
      
      // Finally, disconnect from Plaid
      let endpoint;
      if (hasPlaidItemId) {
        endpoint = `${baseApiUrl}/api/plaid/disconnect/${account.plaidItemId}`;
        console.log('🔑 Using Plaid itemId for disconnection:', account.plaidItemId);
      } else {
        endpoint = `${baseApiUrl}/api/plaid/disconnect-account/${account.plaidAccountId}`;
        console.log('🔑 Using plaidAccountId for disconnection:', account.plaidAccountId);
      }

      console.log('📤 Sending disconnect request to:', endpoint);
      try {
        const response = await axios.delete(endpoint, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('✅ Plaid account disconnected:', response.data);
        return {
          success: true,
          data: response.data
        };
      } catch (apiError) {
        // If 404, it means the account doesn't exist anymore - this is actually a success case
        if (apiError.response && apiError.response.status === 404) {
          console.log('⚠️ Account not found on server, likely already deleted');
          return {
            success: true,
            data: { message: 'Account already deleted or not found' }
          };
        }
        throw apiError;
      }
    } catch (error) {
      console.error('❌ Error disconnecting Plaid account:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Create wallets for all Plaid accounts
  createWalletsForPlaidAccounts: async () => {
    try {
      console.log('🔄 Creating wallets for all Plaid accounts...');
      const token = localStorage.getItem('token');
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/plaid/create-wallets` || 'http://localhost:5000/api/plaid/create-wallets', {}, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Wallets created:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error creating wallets for Plaid accounts:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Get all wallets (personal, joint, partner) in one request
  getAllWallets: async () => {
    try {
      const token = localStorage.getItem('token');
      const partnerWalletEndpoint = import.meta.env.VITE_API_URL 
        ? `${import.meta.env.VITE_API_URL}/api/partners/wallet/all`
        : 'http://localhost:3001/api/partners/wallet/all';
      
      const response = await axios.get(partnerWalletEndpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error fetching all wallets:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Get partner accounts
  getPartnerAccounts: async () => {
    try {
      const token = localStorage.getItem('token');
      const partnerWalletEndpoint = import.meta.env.VITE_API_URL 
        ? `${import.meta.env.VITE_API_URL}/api/partners/wallet/partner-accounts`
        : 'http://localhost:3001/api/partners/wallet/partner-accounts';
      
      const response = await axios.get(partnerWalletEndpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error fetching partner accounts:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },
  
  // Get joint accounts
  getJointAccounts: async () => {
    try {
      const token = localStorage.getItem('token');
      const partnerWalletEndpoint = import.meta.env.VITE_API_URL 
        ? `${import.meta.env.VITE_API_URL}/api/partners/wallet/joint-accounts`
        : 'http://localhost:3001/api/partners/wallet/joint-accounts';
      
      const response = await axios.get(partnerWalletEndpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error fetching joint accounts:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },
  
  // Mark account as joint
  markAccountAsJoint: async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      const partnerWalletEndpoint = import.meta.env.VITE_API_URL 
        ? `${import.meta.env.VITE_API_URL}/api/partners/wallet/${accountId}/mark-as-joint`
        : `http://localhost:3001/api/partners/wallet/${accountId}/mark-as-joint`;
      
      const response = await axios.put(partnerWalletEndpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('❌ Error marking account as joint:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },
  
  // Update account sharing settings
  // Bulk enable sharing for all accounts
  bulkEnableAccountSharing: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.post('/api/partner-wallet/bulk-enable-sharing', {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk enabling account sharing:', error);
      throw error;
    }
  },

  updateSharingSettings: async (accountId, settings) => {
    try {
      const token = localStorage.getItem('token');
      const partnerWalletEndpoint = import.meta.env.VITE_API_URL 
        ? `${import.meta.env.VITE_API_URL}/api/partners/wallet/${accountId}/sharing-settings`
        : `http://localhost:3001/api/partners/wallet/${accountId}/sharing-settings`;
      
      console.log('🚀 Sending sharing settings update:', {
        endpoint: partnerWalletEndpoint,
        accountId,
        settings
      });
      
      const response = await axios.put(partnerWalletEndpoint, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📥 Raw API response:', response.data);
      console.log('📦 Extracted data field:', response.data.data);
      
      // Return the full response data which includes { success, message, data: account }
      return {
        success: response.data.success || true,
        message: response.data.message,
        data: response.data.data || response.data.account // Try both 'data' and 'account' fields
      };
    } catch (error) {
      console.error('❌ Failed to update account sharing settings:', error);
      console.error('❌ Error response:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Get net worth calculation with assets and liabilities
  getNetWorth: async (isPartnerMode = false, isOurMode = false) => {
    try {
      const token = localStorage.getItem('token');
      
      // Determine URL based on mode
      let url;
      if (isPartnerMode) {
        url = `${API_URL}/net-worth?partner=true`;
      } else if (isOurMode) {
        url = `${API_URL}/net-worth?includePartner=true`;
      } else {
        url = `${API_URL}/net-worth`;
      }
      
      console.log('🔄 Fetching net worth calculation - URL:', url, 'isPartnerMode:', isPartnerMode, 'isOurMode:', isOurMode);
      const response = await axios.get(url, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('✅ Net worth data received:', response.data);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('❌ Error fetching net worth:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  // Update loan details for a specific account
  updateLoanDetails: async (accountId, loanDetails) => {
    try {
      console.log('🔄 Updating loan details:', { accountId, loanDetails });
      const token = localStorage.getItem('token');
      
      // Get base URL and ensure it doesn't end with a slash
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      const cleanBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
      
      // Always construct URL as: {base}/api/wallet/{id}/loan-details
      // The base URL is the domain (e.g., https://api.staging.mnymny.com)
      // We always need to add /api/wallet/... to the path
      const apiUrl = `${cleanBase}/api/wallet/${accountId}/loan-details`;
      
      console.log('🔗 Using API URL:', apiUrl);
      
      // Create update data - send the loan details directly
      const updateData = {
          interestRate: loanDetails.interestRate,
          originalPrincipal: loanDetails.originalPrincipal,
          paymentAmount: loanDetails.paymentAmount,
          term: loanDetails.term,
        assetCategory: loanDetails.assetCategory,
        ownershipType: loanDetails.ownershipType, // New: lease, financed, owned
        originatedDate: loanDetails.originatedDate,
        // NEW: Asset value fields
        purchasePrice: loanDetails.purchasePrice,
        currentValue: loanDetails.currentValue,
        // NEW: Lease-specific fields
        leaseEndDate: loanDetails.leaseEndDate,
        buyoutPrice: loanDetails.buyoutPrice,
        mileageLimit: loanDetails.mileageLimit
      };

      console.log('📤 Sending loan details update:', updateData);

      // Use the full API URL directly
      const response = await axios.put(apiUrl, updateData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Loan details updated:', response.data);
      
      // The server returns { success: true, message: ..., data: account }
      // So we want to return the account data directly
      return {
        success: true,
        data: response.data.data  // Extract the account from the nested data
      };
    } catch (error) {
      console.error('❌ Error updating loan details:', error);
      
      // Enhanced error details for debugging
      const errorDetails = {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      };
      
      console.error('Error details:', errorDetails);
      
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        details: errorDetails
      };
    }
  },

  // Get Plaid accounts for debt service integration
  getPlaidAccounts: async (isPartnerMode = false) => {
    try {
      const token = localStorage.getItem('token');
      const url = isPartnerMode ? '/api/plaid/accounts?partner=true' : '/api/plaid/accounts';
      console.log('🏦 Fetching Plaid accounts for debt integration - URL:', url, 'isPartnerMode:', isPartnerMode);
      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Plaid accounts fetched for debt integration:', response.data?.length || 0);
      
      return {
        success: true,
        data: response.data || []
      };
    } catch (error) {
      console.error('❌ Error fetching Plaid accounts for debt integration:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: []
      };
    }
  },

  /**
   * Update Account Ownership Type
   * @param {string} accountId - Account ID
   * @param {object} ownershipData - { ownershipType: 'personal'|'joint', updateExistingTransactions: boolean }
   */
  updateAccountOwnership: async (accountId, ownershipData) => {
    try {
      console.log('🔄 Updating account ownership:', { accountId, ownershipData });
      const token = localStorage.getItem('token');
      
      // Get base URL and ensure it doesn't end with a slash
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      const cleanBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
      
      // Construct URL: {base}/api/wallet/{id}/ownership
      const apiUrl = `${cleanBase}/api/wallet/${accountId}/ownership`;
      
      console.log('🔗 Using API URL:', apiUrl);
      
      const response = await axios.patch(apiUrl, ownershipData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Ownership updated:', response.data);
      
      return {
        success: true,
        data: response.data.account || response.data.data
      };
    } catch (error) {
      console.error('❌ Error updating account ownership:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  }
};

export default walletService; 